import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import PageHeader from "../components/common/PageHeader";
import Button from "../components/common/Button";
import useStore from "../store/useStore";
import api from "../services/api";
import { resolveAssetUrl } from "../utils/assetUrl";

const initialForm = {
  companyName: "",
  email: "",
  phone: "",
  address: "",
  logo: ""
};

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const CompanySettings = () => {
  const { user, authRole, updateUser, toast } = useStore();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const role = authRole || user?.role;
  const queryCompanyId = searchParams.get("companyId");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [noCompany, setNoCompany] = useState(false);
  const [form, setForm] = useState(initialForm);
  const logoPreviewUrl = resolveAssetUrl(form.logo);
  const companyInitial = (form.companyName || user?.companyName || "C").charAt(0).toUpperCase();

  const fetchCompanySettings = useCallback(async () => {
    try {
      setLoading(true);
      setNoCompany(false);
      const url = queryCompanyId
        ? `/companies/${queryCompanyId}`
        : "/companies/my-company";
      const res = await api.get(url);
      const data = res.data?.data || {};

      if (!data.id && !data.companyName && role === "SuperAdmin") {
        setNoCompany(true);
        return;
      }

      setForm({
        companyName: data.companyName || "",
        email: data.email || "",
        phone: data.phone || "",
        address: data.address || "",
        logo: data.logo || ""
      });
    } catch (error) {
      console.error("Error fetching company settings:", error);
      if (role === "SuperAdmin") {
        setNoCompany(true);
        return;
      }
      toast(error.response?.data?.message || "Failed to load company settings", "error");
    } finally {
      setLoading(false);
    }
  }, [toast, queryCompanyId, role]);

  useEffect(() => {
    fetchCompanySettings();
  }, [fetchCompanySettings]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleLogoFileChange = async (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    if (!selectedFile.type.startsWith("image/")) {
      toast("Please choose an image file for logo", "error");
      return;
    }

    const maxSizeBytes = 2 * 1024 * 1024;
    if (selectedFile.size > maxSizeBytes) {
      toast("Logo image must be 2MB or smaller", "error");
      return;
    }

    try {
      const dataUrl = await fileToDataUrl(selectedFile);
      setForm((prev) => ({ ...prev, logo: dataUrl }));
      toast("Logo image selected", "success");
    } catch (error) {
      console.error("Error reading logo image:", error);
      toast("Failed to read logo image", "error");
    }
  };

  const handleSave = async () => {
    if (!form.companyName.trim() || !form.email.trim()) {
      toast("Company name and email are required", "error");
      return;
    }

    try {
      setSaving(true);
      const payload = {
        companyName: form.companyName.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        address: form.address.trim(),
        logo: form.logo.trim()
      };

      const saveUrl = queryCompanyId
        ? `/companies/${queryCompanyId}`
        : "/companies/my-company";
      const res = await api.put(saveUrl, payload);
      const updated = res.data?.data || payload;

      updateUser({
        companyName: updated.companyName,
        companyAddress: updated.address,
        companyPhone: updated.phone,
        companyLogo: updated.logo
      });

      toast("Company settings updated successfully", "success");
    } catch (error) {
      console.error("Error saving company settings:", error);
      toast(error.response?.data?.message || "Failed to update company settings", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div>
        <PageHeader title="Company Settings" />
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 text-gray-600">Loading company settings...</div>
      </div>
    );
  }

  if (noCompany) {
    return (
      <div className="space-y-6">
        <PageHeader title="Company Settings" />
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <h2 className="text-xl font-bold text-gray-900">No company selected</h2>
          <p className="mt-2 text-sm text-gray-600">
            SuperAdmin accounts are not tied to a single company. Pick a company from the companies list to manage its settings
            {queryCompanyId ? `, or check that company ID ${queryCompanyId} exists.` : "."}
          </p>
          <button
            type="button"
            onClick={() => navigate("/superadmin/companies")}
            className="mt-4 px-4 py-2 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700"
          >
            Go to Companies
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <section className="overflow-hidden rounded-3xl border border-teal-100 bg-gradient-to-br from-teal-900 via-slate-900 to-slate-800 text-white shadow-[0_24px_80px_rgba(15,23,42,0.18)]">
        <div className="grid gap-6 px-6 py-8 md:grid-cols-[minmax(0,1.3fr)_minmax(280px,0.9fr)] md:px-8 md:py-10">
          <div className="space-y-4">
            <div className="inline-flex rounded-full border border-white/15 bg-white/10 px-4 py-1 text-xs font-semibold uppercase tracking-[0.24em] text-teal-100">
              Company profile
            </div>
            <div>
              <h1 className="text-3xl font-black tracking-tight md:text-4xl">Company Settings</h1>
              <p className="mt-3 max-w-2xl text-sm leading-6 text-teal-50/90 md:text-base">
                Keep your official company details polished for invoices, receipts, and shared documents across the platform.
              </p>
            </div>
          </div>

          <div className="rounded-2xl border border-white/10 bg-white/10 p-5 backdrop-blur-sm">
            <div className="flex items-center gap-4">
              {logoPreviewUrl ? (
                <img src={logoPreviewUrl} alt="Company logo preview" className="h-16 w-16 rounded-2xl bg-white object-contain p-2 ring-1 ring-white/15" />
              ) : (
                <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-white/15 text-2xl font-black text-white ring-1 ring-white/15">
                  {companyInitial}
                </div>
              )}
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-teal-100/80">Primary company record</p>
                <p className="text-lg font-bold text-white">{form.companyName || user?.companyName || "Company Name"}</p>
                <p className="text-sm text-teal-50/80">{form.email || user?.email || "Company email"}</p>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
              <div className="rounded-xl bg-white/10 p-3">
                <p className="text-xs text-teal-100/80">Phone</p>
                <p className="mt-1 font-semibold text-white">{form.phone || "N/A"}</p>
              </div>
              <div className="rounded-xl bg-white/10 p-3">
                <p className="text-xs text-teal-100/80">Address</p>
                <p className="mt-1 max-h-10 overflow-hidden font-semibold leading-5 text-white">{form.address || "N/A"}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm md:p-8">
        <div className="mb-6 flex items-center justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Company details</h2>
            <p className="text-sm text-gray-600">Update the identity and contact information your customers see.</p>
          </div>
          <div className="rounded-full bg-teal-50 px-3 py-1.5 text-xs font-semibold text-teal-700 ring-1 ring-teal-100">
            Editable record
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Company Name <span className="text-red-500">*</span></label>
            <input
              type="text"
              name="companyName"
              value={form.companyName}
              onChange={onChange}
              placeholder="Your registered company name"
              className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Email <span className="text-red-500">*</span></label>
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={onChange}
              placeholder="info@company.com"
              className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Phone</label>
            <input
              type="text"
              name="phone"
              value={form.phone}
              onChange={onChange}
              placeholder="+220 123 4567"
              className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Address</label>
            <input
              type="text"
              name="address"
              value={form.address}
              onChange={onChange}
              placeholder="Street, Town, Country"
              className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>

          <div className="md:col-span-2">
            <p className="text-sm text-gray-600">Rent, invoices, and balances are in Dalasis. A rental payment can still be recorded in dollars on the Payments page.</p>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Logo Image</label>
            <input
              type="file"
              accept="image/*"
              onChange={handleLogoFileChange}
              className="w-full px-4 py-2.5 rounded-lg border border-gray-300 bg-white"
            />
            <p className="text-xs text-gray-500 mt-2">Upload PNG, JPG, or SVG up to 2MB.</p>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Logo URL</label>
            <input
              type="text"
              name="logo"
              value={form.logo}
              onChange={onChange}
              placeholder="https://example.com/logo.png"
              className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
            <p className="text-xs text-gray-500 mt-2">You can paste an image URL or use the file picker above.</p>
          </div>
        </div>

        <div className="mt-6">
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save Company Settings"}
          </Button>
        </div>
      </section>

      <section className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
        <h3 className="mb-3 text-lg font-bold text-gray-900">Document Preview</h3>
        <div className="flex flex-col gap-4 rounded-2xl border border-dashed border-gray-300 p-4 sm:flex-row sm:items-center">
          {logoPreviewUrl ? (
            <img src={logoPreviewUrl} alt="Company logo preview" className="h-16 w-16 rounded-xl border border-gray-200 object-contain bg-white p-2" />
          ) : (
            <div className="flex h-16 w-16 items-center justify-center rounded-xl bg-teal-100 text-xl font-bold text-teal-700">
              {companyInitial}
            </div>
          )}
          <div className="text-sm">
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-teal-600">Business card</div>
            <div className="mt-1 text-base font-bold text-gray-900">{form.companyName || "Company Name"}</div>
            <div className="text-gray-600">{form.address || "Address"}</div>
            <div className="text-gray-600">{form.phone || "Phone"}{form.email ? ` | ${form.email}` : ""}</div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CompanySettings;
